import { IEquipment } from '@models/Equipment.model';
import { IEquipmentInventory } from '@models/EquipmentInventory.model';
import { IFieldApiModel2 } from '@models/Field.model';
import {
  EquipmentFieldConfigurationTypeOptions,
  IFieldConfiguration,
} from '@models/FieldConfiguration.model';
import { formatDate } from '@utils';
import { isNonNullable } from '@utils/isNonNullable';
import { lengthFromDays } from '@utils/time/lengthFromDays';
import { format } from 'date-fns';
import { hasDateName } from '../hasDateName/hasDateName';

// eslint-disable-next-line @typescript-eslint/ban-types
type NonNull<T extends object> = {
  [P in keyof T]: NonNullable<T[P]>;
};

type ValidField = NonNull<IFieldApiModel2>;
function isValid(t: IFieldApiModel2): t is ValidField {
  return t.content !== null && t.customFieldId !== null && t.id !== null;
}
export type EquipmentDataKeys =
  | EquipmentFieldConfigurationTypeOptions
  | 'fields';

type EquipmentData = Pick<IEquipment | IEquipmentInventory, EquipmentDataKeys>;

type SomeFieldConfig = Pick<IFieldConfiguration, 'customField' | 'fieldName'>;

export const DATE_FORMAT_EXPANDED = 'MMM do yyyy';
export const getEquipmentValue = (
  itemConfiguration: SomeFieldConfig,
  equipmentData: EquipmentData,
): string => {
  const { fieldName, customField } = itemConfiguration;
  if (customField) {
    const assetNumber = equipmentData.fields.find(field => {
      return (
        field.customFieldId === customField?.id && isValid(field) // We use default value if this fails anyways, so we assert that the matched field is non-null here.
      );
    }) as ValidField | undefined;
    if (
      assetNumber &&
      customField.type === 'date' &&
      formatDate(new Date(Date.parse(assetNumber.content))) !== null
    ) {
      return format(new Date(Date.parse(assetNumber.content)), 'MMM do yyyy');
    }
    if (assetNumber) return assetNumber.content;
    return customField.defaultValue ? customField.defaultValue : '';
  }
  if (fieldName) {
    const typedFieldName = fieldName as EquipmentFieldConfigurationTypeOptions;
    const containsDateName = hasDateName(fieldName);
    const containsPriceName = fieldName.includes('Price');
    const typedFieldValue = equipmentData[typedFieldName];
    let value = typedFieldValue;

    const priceFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (typedFieldName === 'productWarranty' && typedFieldValue) {
      value = lengthFromDays(typedFieldValue as number);
    }
    if (containsDateName && typedFieldValue) {
      value = format(typedFieldValue as Date, DATE_FORMAT_EXPANDED);
    }
    if (containsPriceName && isNonNullable(typedFieldValue)) {
      value = priceFormatter.format(typedFieldValue as number);
    }
    if (isNonNullable(value)) {
      return value.toString().replaceAll('&amp;', '&');
    }
  }

  return '';
};
